import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { Trash2, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AdminContent() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 15;

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/content/posts', { params: { skip, limit } });
      setPosts(res.data.posts);
      setTotal(res.data.total);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const deletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await apiClient.delete(`/admin/content/posts/${postId}`);
      fetchPosts();
    } catch (err) {
      // silent
    }
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-content">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white mb-1">Content Management</h1>
          <p className="text-white/40 text-sm">{total} community posts</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : posts.length > 0 ? (
          <>
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.post_id}
                  className="bg-[#161821] border border-white/5 rounded-2xl p-5 hover:border-white/10 transition-colors"
                  data-testid={`post-${post.post_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          post.category === 'support' ? 'bg-rose-500/10 text-rose-400' :
                          post.category === 'inspiration' ? 'bg-amber-500/10 text-amber-400' :
                          post.category === 'discussion' ? 'bg-blue-500/10 text-blue-400' :
                          'bg-white/5 text-white/40'
                        }`}>
                          {post.category}
                        </span>
                        <span className="text-white/30 text-xs">by {post.user_name}</span>
                        <span className="text-white/20 text-xs">{post.created_at ? new Date(post.created_at).toLocaleDateString() : ""}</span>
                      </div>
                      <h3 className="text-white font-medium mb-1">{post.title}</h3>
                      <p className="text-white/50 text-sm line-clamp-2">{post.content}</p>
                      <div className="flex items-center gap-4 mt-3 text-white/30 text-xs">
                        <span>{post.likes || 0} likes</span>
                        <span>{post.comments_count || 0} comments</span>
                      </div>
                    </div>
                    <button
                      onClick={() => deletePost(post.post_id)}
                      className="p-2 rounded-xl hover:bg-red-500/10 text-white/20 hover:text-red-400 transition-colors flex-shrink-0"
                      title="Delete post"
                      data-testid={`delete-post-${post.post_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-white/40 text-sm">Page {currentPage} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    disabled={skip === 0}
                    className="border-white/10 text-white/50 hover:text-white rounded-xl"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkip(skip + limit)}
                    disabled={skip + limit >= total}
                    className="border-white/10 text-white/50 hover:text-white rounded-xl"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-[#161821] border border-white/5 rounded-2xl p-12 text-center">
            <MessageSquare className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/30">No community posts yet</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
